import { FileInput, FileType } from "components/FileInput/FileInput";
import { Preview } from "components/PreviewModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  useDeleteOpenItemInvoiceMutation,
  useGetOpenItemsQuery,
  useUploadOpenItemInvoiceMutation,
} from "store/apis/openItem";
import { RootState } from "store/store";
import { OpenItem } from "types/Models/openItem";

type OpenItemInvoiceInputProps = {
  openItem: OpenItem;
  variant: "default" | "icon";
};

export const OpenItemInvoiceInput = ({
  openItem,
  variant,
}: OpenItemInvoiceInputProps) => {
  const { alertToast, successToast } = useToast();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const [uploadOpenItemInvoice, { isLoading: isUploading }] =
    useUploadOpenItemInvoiceMutation();

  const [deleteOpenItemInvoice, { isLoading: isDeleting }] =
    useDeleteOpenItemInvoiceMutation();

  const { activeChannelGroupId: groupId } = useSelector(
    (state: RootState) => state.reviewAndBalancePayment
  );

  const { uuid, csv_id: csvId, invoice } = openItem;

  const { data: uploadedCSV } = useGetOpenItemsQuery(
    { csvId, groupId },
    { skip: !groupId || !csvId, refetchOnMountOrArgChange: true }
  );

  const { status } = uploadedCSV || {};

  const uploadInvoice = async ({
    openItemId,
    file,
  }: {
    openItemId: string;
    file: FileType;
  }) => {
    try {
      await uploadOpenItemInvoice({
        groupId,
        openItemId,
        file: file as File,
      }).unwrap();
      successToast({ message: "File Uploaded" });
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const onInvoiceDelete = async ({ openItemId }: { openItemId: string }) => {
    try {
      await deleteOpenItemInvoice({ openItemId, groupId }).unwrap();
      successToast({ message: "File Deleted" });
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const closePreview = () => setPreviewFileId(null);

  const onPreview = ({ fileId }: { fileId: string }) => {
    setPreviewFileId(fileId);
  };

  if (status === "SUBMITTED" && !Boolean(invoice)) {
    return <>-</>;
  }

  return (
    <>
      <FileInput
        file={invoice}
        onDrop={(files) => uploadInvoice({ openItemId: uuid, file: files[0] })}
        isUploading={isUploading}
        variant={variant}
        onFileClick={({ uuid: fileId }) => onPreview({ fileId })}
        isDeleting={isDeleting}
        onDelete={
          status === "REQUESTED"
            ? () => onInvoiceDelete({ openItemId: uuid })
            : undefined
        }
      />
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={closePreview}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};
